import React, { useRef } from 'react'
import { styled, css, keyframes } from 'styled-components'
import { MissionFrame as Frame, BasicFrame, SurveyFrame } from '../frames'
import Accessibility from './Accessibility'
import LoginView from './LoginView'
import {
	getScreen,
	useIsCharacterOverlay,
	useMissionStateSelector,
	useIsSilent,
	useShowAccessibility,
	useIsShowingASLVideo,
	useLocalStateSelector,
	getIsShaking,
	showDeathScreen,
	shouldShowSequenceFrame,
	shouldShowSurvey,
	shouldShowGeneratingAnalytics,
	useWasAnalyticsReceived,
	getDeathState,
} from '../../context'
import SizingWrapper from '../basics/SizingWrapper'
import Notifications from './Notifications/Notifications'
import Screen, { DeathScreen, PauseScreen } from './Screens'
import { useFixedContainerSize } from './Screens/hook'
import { VIDEO_HEIGHT_OF_TOTAL_SCREEN_HEIGHT_PERCENTAGE } from '../../utility/constants'
import VideoImageOverlay from './Overlays/VideoImageOverlay'
import Static from './Static'
import EndOfMissionSequence from '../frames/EndSequenceFrame'
import CanvasImageUploader from './Screens/CollaborativeCulminatingMomentScreen/CanvasImageUploader'
import { DIALOG_SIBLING_ID } from '../basics/Dialog.jsx'

type Props = {
	handleError?: (message: string) => void,
	wrapperSize: { width: number, height: number },
	shouldShowLogin: boolean,
}

export default function PlayerView({
	handleError,
	wrapperSize,
	shouldShowLogin,
}: Props): React$Node {
	const analyticsReceived = useWasAnalyticsReceived()
	const screen = useMissionStateSelector(getScreen)
	const showAccessibility = useShowAccessibility()
	const showASLVideo = useIsShowingASLVideo()
	const isShaking = useLocalStateSelector(getIsShaking)
	const isCharacterOverlay = useIsCharacterOverlay()
	const crewDead = useLocalStateSelector(showDeathScreen)
	const showSequenceFrame = useMissionStateSelector(
		shouldShowSequenceFrame(analyticsReceived)
	)
	const showSurvey = useMissionStateSelector(shouldShowSurvey)
	const showGeneratingAnalytics = useMissionStateSelector(
		shouldShowGeneratingAnalytics(analyticsReceived)
	)
	const deathState = useMissionStateSelector(getDeathState)
	const localContainerRef = useRef(null)

	const { height, width } = useFixedContainerSize(
		wrapperSize.width,
		wrapperSize.height
	)

	const isSilent = useIsSilent()

	const renderShakableParts = () => {
		const showShrunkScreen = showASLVideo && !isCharacterOverlay
		return (
			<ShakableDiv $shaking={isShaking} ref={localContainerRef}>
				{isShaking && <Static />}
				{crewDead && deathState ? (
					<DeathScreen height={height} width={width} deathState={deathState} />
				) : (
					<ScreenFrameContainer height={height} width={width}>
						<ScreenContainer
							$shrink={showShrunkScreen}
							height={height}
							width={width}>
							{screen && <Screen screen={screen} showASLVideo={showASLVideo} />}
						</ScreenContainer>
						{showShrunkScreen && <HeightPlaceholder />}

						{showAccessibility && <Accessibility showASLVideo={showASLVideo} />}
						<VideoImageOverlay handleError={handleError} />
						<Frame />
						{!isSilent && <Notifications />}
					</ScreenFrameContainer>
				)}
			</ShakableDiv>
		)
	}

	return (
		<SizingWrapper width={width} height={height} id={DIALOG_SIBLING_ID}>
			{shouldShowLogin ? (
				<LoginView />
			) : showSequenceFrame || showSurvey || showGeneratingAnalytics ? (
				<BasicFrame>
					{(showSurvey || showGeneratingAnalytics) && (
						<SurveyFrame generatingAnalytics={showGeneratingAnalytics} />
					)}
					{showSequenceFrame && <EndOfMissionSequence />}
				</BasicFrame>
			) : (
				<>
					<CanvasImageUploader />
					<PauseScreen />
					<PartsWrapper>{renderShakableParts()}</PartsWrapper>
				</>
			)}
		</SizingWrapper>
	)
}

const SHRUNK_SCREEN_SIZE_PERCENTAGE = 0.3

const HeightPlaceholder = styled.div`
	height: ${VIDEO_HEIGHT_OF_TOTAL_SCREEN_HEIGHT_PERCENTAGE * 100}%;
`

const ScreenContainer = styled.div`
	display: flex;
	justify-content: center;
	height: ${VIDEO_HEIGHT_OF_TOTAL_SCREEN_HEIGHT_PERCENTAGE * 100}%;

	${({ height, width, $shrink, theme }) =>
		$shrink &&
		`
		height: ${
			height *
			VIDEO_HEIGHT_OF_TOTAL_SCREEN_HEIGHT_PERCENTAGE *
			SHRUNK_SCREEN_SIZE_PERCENTAGE
		}px;
		width: ${width * SHRUNK_SCREEN_SIZE_PERCENTAGE}px;
		top: 2%;
		left: 2%;
		position: absolute;
		z-index: ${theme.zTopOverlay};
		background-color: ${theme.base4};
		border-radius: 4px;
	`}
`

const ScreenFrameContainer = styled.div`
	position: relative;
	overflow: hidden;
	${({ height, width }) =>
		`
		min-height: ${height}px;
		min-width: ${width}px;
	`};
	width: 100%;
	height: 100%;
`

const PartsWrapper = styled.div`
	position: relative;
	flex: 1;
	overflow: hidden;
`

const shake = keyframes`
	0% {
		transform: translate(1px, 1px) rotate(0deg);
	}
	10% {
		transform: translate(-1px, -2px) rotate(-0.5deg);
	}
	20% {
		transform: translate(-2px, 0px) rotate(0.5deg);
	}
	30% {
		transform: translate(1px, 2px) rotate(0deg);
	}
	40% {
		transform: translate(1px, -1px) rotate(0.5deg);
	}
	50% {
		transform: translate(-1px, 2px) rotate(-0.5deg);
	}
	60% {
		transform: translate(-0px, 1px) rotate(0deg);
	}
	70% {
		transform: translate(2px, 1px) rotate(-0.5deg);
	}
	80% {
		transform: translate(-1px, -1px) rotate(0.5deg);
	}
	90% {
		transform: translate(1px, 0px) rotate(0deg);
	}
	100% {
		transform: translate(1px, -2px) rotate(-0.5deg);
	}
`

const ShakableDiv = styled.div`
	display: flex;
	justify-content: center;
	height: 100%;
	width: 100%;
	${({ $shaking }) =>
		$shaking &&
		css`
			animation: ${shake} 0.5s infinite;
		`};
`
